import { CantileverButton } from 'components/CantileverButton'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { TextInput } from 'components/form/inputs/TextInput'
import {
  NodePropertyPanelProvider,
  useNodePropertyPanel,
} from 'features/processFlowDiagrams/components/nodes/propertyPanel/NodePropertyPanelContext'
import { DatapointSelector } from 'features/systemLens'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { PfdNode } from 'types'

import {
  FormSection,
  InputColumn,
  InputRow,
  PanelContainer,
  PanelHeader,
  PanelPlaceholder,
  PanelWrapper,
  PercentageControls,
  PercentageDisplay,
  rotationOptions,
  SectionTitle,
  ToggleButton,
} from './StylesAndHelper'

interface NodePropertyPanelProps {
  selectedNode: PfdNode
}

const entryTypeOptions = [
  { label: 'Unit', value: 'unit' },
  { label: 'PFD', value: 'pfd' },
]

const PanelContent = ({ selectedNode }: { selectedNode: PfdNode }) => {
  const {
    label,
    xPosition,
    yPosition,
    scale,
    rotation,
    selectedEntryType,
    unitOptions,
    selectedTags,
    setSelectedTags,
    displayPanel,
    setDisplayPanel,
    siteID,
    handleLabelChange,
    handlePositionXChange,
    handlePositionYChange,
    handleRotationChange,
    increaseSize,
    decreaseSize,
    setSelectedEntryType,
    handleLinkChange,
    handleDatapointChange,
    isLoadingDatapoints,
    datapoints,
  } = useNodePropertyPanel()

  return (
    <PanelWrapper>
      <ToggleButton onClick={() => setDisplayPanel(!displayPanel)}>
        {displayPanel ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </ToggleButton>

      {displayPanel ? (
        <PanelContainer>
          <PanelHeader>{selectedNode.data.label}</PanelHeader>
          <FormSection>
            <SectionTitle>Label</SectionTitle>
            <TextInput
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder='Label'
            />
          </FormSection>
          <FormSection>
            <SectionTitle>Size</SectionTitle>
            <PercentageControls>
              <CantileverButton
                onClick={decreaseSize}
                disabled={scale <= 20}
                title='Decrease size by 10%'
                iconOnly
                square>
                −
              </CantileverButton>
              <PercentageDisplay>{scale}%</PercentageDisplay>
              <CantileverButton
                onClick={increaseSize}
                disabled={scale >= 1000}
                title='Increase size by 10%'
                iconOnly
                square>
                +
              </CantileverButton>
            </PercentageControls>
          </FormSection>

          <FormSection>
            <SectionTitle>Position</SectionTitle>
            <InputRow>
              <InputColumn>
                <TextInput
                  $label='X'
                  value={String(xPosition || '')}
                  onChange={(e) => handlePositionXChange(e.target.value)}
                  placeholder='X Position'
                />
              </InputColumn>
              <InputColumn>
                <TextInput
                  $label='Y'
                  value={String(yPosition || '')}
                  onChange={(e) => handlePositionYChange(e.target.value)}
                  placeholder='Y Position'
                />
              </InputColumn>
            </InputRow>
          </FormSection>

          <FormSection>
            <SectionTitle>Rotation</SectionTitle>
            <Dropdown
              options={rotationOptions}
              value={String(rotation || '0')}
              onValueChange={(value) => handleRotationChange(value)}
              placeholder='Select rotation'
            />
          </FormSection>

          <FormSection>
            <SectionTitle>Link</SectionTitle>
            <Dropdown
              key={`${selectedNode.id}-entry-type`}
              $label='Entry Type'
              options={entryTypeOptions}
              onValueChange={setSelectedEntryType}
              placeholder='Select entry type'
              defaultValue={selectedNode.data.link?.entryType ?? ''}
            />
            {selectedEntryType === 'unit' && unitOptions && (
              <Dropdown
                key={`${selectedNode.id}-unit`}
                $label='Unit'
                options={unitOptions}
                onValueChange={(value) =>
                  handleLinkChange({
                    entryType: selectedEntryType,
                    entryID: value,
                    siteID: siteID,
                  })
                }
                placeholder='Select unit'
                defaultValue={selectedNode.data.link?.entryID ?? ''}
              />
            )}
          </FormSection>

          {selectedNode.data.link?.entryType === 'unit' && (
            <FormSection>
              <SectionTitle>Bindings</SectionTitle>
              {/* <MultiSelect // TODO: Uncomment this when we have a way to bind to Maximo assets */}
              {/*   key={`${selectedNode.id}-asset-lens-location`} */}
              {/*   $label='Maximo Asset Binding' */}
              {/*   options={assetLensLocationOptions} */}
              {/*   onValueChange={handleAssetLensLocationChange} */}
              {/*   defaultValue={selectedNode.data.assetLensLocations} */}
              {/*   placeholder='Select Maximo asset' */}
              {/* /> */}
              {/* <MultiSelect
                key={`${selectedNode.id}-datapoint`}
                $label='Datapoints Binding'
                options={datapointOptions}
                onValueChange={handleDatapointChange}
                defaultValue={selectedNode.data.datapoints}
                placeholder='Select Datapoints'
              /> */}
              <DatapointSelector
                tagsOptions={datapoints}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                isLoadingSensors={isLoadingDatapoints}
                onToggleTags={handleDatapointChange}
                onRemoveTags={handleDatapointChange}
              />
            </FormSection>
          )}
        </PanelContainer>
      ) : (
        <PanelPlaceholder />
      )}
    </PanelWrapper>
  )
}

const NodePropertyPanel = ({ selectedNode }: NodePropertyPanelProps) => {
  return (
    <NodePropertyPanelProvider selectedNode={selectedNode}>
      <PanelContent selectedNode={selectedNode} />
    </NodePropertyPanelProvider>
  )
}

export default NodePropertyPanel
